import { useEffect, useMemo, useState } from "react";
import Header from "../components/common/header";
import Footer from "../components/common/footer";
import WalletReadiness from "../components/WalletReadiness";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  PauseCircle,
  Shield,
  TrendingUp,
  Wallet,
  XCircle,
} from "lucide-react";

const STATE_URL = "/auto-trader-state.json";
const BOT_NETWORKS = [
  { key: "ethereum", label: "Ethereum Bot", route: "Uniswap / ParaSwap route", swap: "active" },
  { key: "base", label: "Base Bot", route: "Uniswap / 0x / ParaSwap adapter", swap: "pending" },
  { key: "arbitrum", label: "Arbitrum Bot", route: "Uniswap / 0x / ParaSwap adapter", swap: "pending" },
  { key: "polygon", label: "Polygon Bot", route: "QuickSwap route", swap: "active" },
  { key: "bnb", label: "BNB Bot", route: "PancakeSwap / 0x / ParaSwap adapter", swap: "pending" },
  { key: "optimism", label: "Optimism Bot", route: "Velodrome / Uniswap adapter", swap: "pending" },
];

function money(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(number);
}

function pct(value) {
  return `${(Number(value || 0) * 100).toFixed(2)}%`;
}

function short(value) {
  if (!value) return "unknown";
  return value.length > 14 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function age(value) {
  if (!value) return "-";
  const diff = Date.now() - new Date(value).getTime();
  if (diff < 0) return "now";
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.floor(min / 60)}h ago`;
}

function healthLabel(health) {
  if (!health) return "waiting";
  return health.status === "ok" ? "ok" : health.wsStatus || health.status;
}

function modeLabel(mode) {
  return mode === "paper" || !mode ? "TEST MODE" : String(mode).toUpperCase();
}

export default function AutoTradingPage() {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await fetch(`${STATE_URL}?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!response.ok) throw new Error("state unavailable");
        const next = await response.json();
        if (!cancelled) {
          setState(next);
          setError("");
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Auto-trader state is not available yet.");
          setLoading(false);
        }
      }
    }

    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const summary = useMemo(() => {
    const positions = state?.positions || [];
    const closed = state?.closedTrades || [];
    const realized = Number(state?.stats?.realizedPnlUsd || 0);
    const unrealized = positions.reduce(
      (sum, item) => sum + Number(item.unrealizedPnlUsd || 0),
      0,
    );
    const wins = closed.filter((item) => Number(item.pnlUsd || 0) > 0).length;
    return {
      positions,
      closed,
      realized,
      unrealized,
      totalPnl: realized + unrealized,
      winRate: closed.length ? wins / closed.length : 0,
    };
  }, [state]);

  const botRows = useMemo(() => {
    const positions = summary.positions || [];
    const rejected = state?.rejectedSignals || [];
    return BOT_NETWORKS.map((network) => {
      const open = positions.filter((item) => item.net === network.key);
      const rejectedCount = rejected.filter((item) => item.net === network.key).length;
      const entries = positions.filter((item) => item.net === network.key).length +
        (state?.closedTrades || []).filter((item) => item.net === network.key).length;
      const latest = open[0];
      return {
        ...network,
        open,
        rejectedCount,
        entries,
        latest,
      };
    });
  }, [state, summary.positions]);

  return (
    <div className="auto-trading-page">
      <Header />
      <main className="auto-trading-shell">
        <section className="auto-trading-hero">
          <div>
            <span className="auto-eyebrow">Test execution engine</span>
            <h1>Automatic trading, wired to live ChainIntel signals.</h1>
            <p>
              This dashboard follows the live signal stream and shows how the
              strategy engine would trade with test capital before real wallet
              execution is enabled.
            </p>
          </div>
          <div className={`auto-status-card ${state?.mode || "paper"}`}>
            <Shield size={28} />
            <span>Mode</span>
            <strong>{modeLabel(state?.mode)}</strong>
            <small>Read-only dashboard. No live transactions.</small>
          </div>
        </section>

        <WalletReadiness />

        {loading ? (
          <div className="auto-empty">Loading auto-trader state...</div>
        ) : error ? (
          <div className="auto-empty danger">{error}</div>
        ) : (
          <>
            <section className="auto-stats-grid">
              <Stat icon={<Wallet />} label="Test balance" value={money(state?.balances?.USDC)} />
              <Stat icon={<TrendingUp />} label="Total PnL" value={money(summary.totalPnl)} tone={summary.totalPnl >= 0 ? "good" : "bad"} />
              <Stat icon={<Activity />} label="Signals processed" value={state?.stats?.signals || 0} />
              <Stat icon={<BarChart3 />} label="Win rate" value={pct(summary.winRate)} />
              <Stat icon={<PauseCircle />} label="Open positions" value={summary.positions.length} />
              <Stat icon={<XCircle />} label="Rejected signals" value={state?.stats?.rejected || 0} />
            </section>

            <section className="auto-grid-two compact">
              <Panel title="Risk Limits" icon={<Shield />}>
                <div className="risk-limit-grid">
                  <RiskLimit label="Mode" value="Test only" />
                  <RiskLimit label="Max positions" value="30 total" />
                  <RiskLimit label="Per network" value="5 open" />
                  <RiskLimit label="Position size" value="$100" />
                  <RiskLimit label="Stop loss" value="-1.5%" />
                  <RiskLimit label="Take profit" value="2.0%" />
                  <RiskLimit label="Max hold" value="5m" />
                  <RiskLimit label="Token risk" value="Required" />
                </div>
              </Panel>

              <Panel title="Live Checklist" icon={<CheckCircle2 />}>
                <div className="live-checklist">
                  <CheckItem tone="ok" title="Test mode active" />
                  <CheckItem tone="ok" title="Live execution disabled" />
                  <CheckItem tone="ok" title="Private key not loaded in UI" />
                  <CheckItem tone="ok" title="Token Risk service required" />
                  <CheckItem tone="hold" title="External audit required before live" />
                </div>
              </Panel>
            </section>

            <section className="bot-card-grid">
              {botRows.map((bot) => (
                <BotCard key={bot.key} bot={bot} />
              ))}
            </section>

            <section className="auto-grid-two">
              <Panel title="Open Positions" icon={<Clock />}>
                {summary.positions.length ? (
                  summary.positions.map((position) => (
                    <PositionRow key={position.id} position={position} />
                  ))
                ) : (
                  <Empty text="No test positions open right now." />
                )}
              </Panel>

              <Panel title="Recent Decisions" icon={<CheckCircle2 />}>
                {summary.closed.length ? (
                  summary.closed.slice(0, 8).map((trade) => (
                    <TradeRow key={trade.id} trade={trade} />
                  ))
                ) : (
                  <Empty text="Closed test trades will appear here." />
                )}
              </Panel>
            </section>

            <section className="auto-grid-two">
              <Panel title="Risk Blocks" icon={<AlertTriangle />}>
                {(state?.rejectedSignals || []).slice(0, 10).map((item, index) => (
                  <div className="auto-list-row rejected" key={`${item.txHash}-${index}`}>
                    <div>
                      <strong>{item.reason}</strong>
                      <span>{item.event} on {item.net}</span>
                    </div>
                    <b>{item.score}</b>
                  </div>
                ))}
              </Panel>

              <Panel title="Engine State" icon={<Shield />}>
                <div className="auto-engine-state">
                  <div><span>Last update</span><strong>{age(state?.updatedAt)}</strong></div>
                  <div><span>Feed</span><strong>{healthLabel(state?.health)}</strong></div>
                  <div><span>WS</span><strong>{state?.health?.wsStatus || "-"}</strong></div>
                  <div><span>Last signal</span><strong>{age(state?.health?.lastSignalAt)}</strong></div>
                  <div><span>Entries</span><strong>{state?.stats?.entries || 0}</strong></div>
                  <div><span>Exits</span><strong>{state?.stats?.exits || 0}</strong></div>
                  <div><span>Daily PnL</span><strong>{money(state?.stats?.dailyPnlUsd)}</strong></div>
                  <div><span>PID</span><strong>{state?.health?.pid || "-"}</strong></div>
                </div>
                <pre>{JSON.stringify(state?.stats?.lastDecision || {}, null, 2)}</pre>
              </Panel>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Stat({ icon, label, value, tone = "" }) {
  return (
    <article className={`auto-stat ${tone}`}>
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function Panel({ title, icon, children }) {
  return (
    <section className="auto-panel">
      <div className="auto-panel-head">
        <div>{icon}<h2>{title}</h2></div>
      </div>
      <div className="auto-panel-body">{children}</div>
    </section>
  );
}

function PositionRow({ position }) {
  const pnl = Number(position.unrealizedPnlUsd || 0);
  return (
    <div className="auto-list-row">
      <div>
        <strong>{position.tokenSymbol || "UNKNOWN"} <code>{short(position.tokenAddress)}</code></strong>
        <span>{position.net} | score {position.entryScore} | {age(position.openedAt)}</span>
      </div>
      <b className={pnl >= 0 ? "good" : "bad"}>{money(pnl)}</b>
    </div>
  );
}

function TradeRow({ trade }) {
  const pnl = Number(trade.pnlUsd || 0);
  return (
    <div className="auto-list-row">
      <div>
        <strong>{trade.exitReason} <code>{short(trade.tokenAddress)}</code></strong>
        <span>{trade.net} | {trade.entryEvent} -> {trade.exitEvent}</span>
      </div>
      <b className={pnl >= 0 ? "good" : "bad"}>{money(pnl)}</b>
    </div>
  );
}

function Empty({ text }) {
  return <div className="auto-empty small">{text}</div>;
}

function RiskLimit({ label, value }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function CheckItem({ tone, title }) {
  return (
    <div className={tone}>
      <span>{tone === "ok" ? "OK" : "HOLD"}</span>
      <strong>{title}</strong>
    </div>
  );
}

function BotCard({ bot }) {
  const latest = bot.latest;
  return (
    <article className="bot-card">
      <div className="bot-card-head">
        <div>
          <h3>{bot.label}</h3>
          <p>limit 5 open positions</p>
        </div>
        <strong>{bot.open.length}/5</strong>
      </div>
      <div className="bot-tags">
        <span>Signals</span>
        <span>Test bot</span>
        <span className={bot.swap === "active" ? "active" : "pending"}>
          Swap {bot.swap}
        </span>
        <span className="pending">Test wallet pending</span>
      </div>
      <p className="bot-route">{bot.route}</p>
      <div className="bot-metrics">
        <div><span>Entries</span><strong>{bot.entries}</strong></div>
        <div><span>Open</span><strong>{bot.open.length}</strong></div>
        <div><span>Rejected</span><strong>{bot.rejectedCount}</strong></div>
      </div>
      {latest ? (
        <div className="bot-latest">
          <div>
            <strong>{latest.tokenSymbol || "UNKNOWN"}</strong>
            <b>{money(latest.unrealizedPnlUsd)}</b>
          </div>
          <span>score {latest.entryScore} | {latest.entryEvent} | {age(latest.openedAt)}</span>
          <em>entry {money(latest.entryPrice)} | mark {money(latest.markPrice)}</em>
        </div>
      ) : (
        <div className="bot-empty">Waiting for a qualified signal.</div>
      )}
    </article>
  );
}
